import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from "react";
import GiftIcon from "./GiftIcon";

/**
 * GiftBurst — global overlay that plays whenever a gift is sent/received.
 *
 * Three modes:
 *   - inline  (default rarity ≤ epic): big icon + name floats up from
 *             bottom-right corner, particles fly out, auto-dismiss in 3.6s.
 *   - hero    (legendary): icon takes 30% of viewport, particles, dim backdrop.
 *   - feast   (mythic / full_screen): full-screen takeover with Hidden Feast
 *             style stagger — burger, pizza, donut, cupcake confetti.
 */
const GiftBurstCtx = createContext({ trigger: () => {} });

export function useGiftBurst() {
  return useContext(GiftBurstCtx);
}

function Particle({ angle, distance, gift }) {
  const dx = Math.cos(angle) * distance;
  const dy = Math.sin(angle) * distance;
  return (
    <span
      className="gift-burst-particle absolute top-1/2 left-1/2 text-3xl pointer-events-none"
      style={{ "--dx": `${dx}px`, "--dy": `${dy}px` }}
    >
      {gift.emoji}
    </span>
  );
}

function InlineBurst({ burst, onDone }) {
  const { gift, sender_name, message } = burst;
  useEffect(() => {
    const t = setTimeout(onDone, 3600);
    return () => clearTimeout(t);
  }, [onDone]);

  // 12 particles in a circle (lazy init so we generate once)
  const [particles] = useState(() =>
    Array.from({ length: 12 }, (_, i) => ({
      angle: (i / 12) * Math.PI * 2,
      distance: 80 + Math.random() * 60,
    }))
  );
  return (
    <div className="pointer-events-none fixed bottom-24 right-6 z-[80] flex flex-col items-end gap-2">
      <div className="relative w-32 h-32 grid place-items-center">
        {particles.map((p, i) => (
          <Particle key={i} angle={p.angle} distance={p.distance} gift={gift} />
        ))}
        <div className={`rarity-${gift.rarity || "common"} rounded-full p-3 bg-zinc-950/80 backdrop-blur-sm border border-white/10 grid place-items-center`}>
          <GiftIcon gift={gift} size={64} />
        </div>
      </div>
      <div className="gift-float-up rounded-2xl px-4 py-2.5 bg-zinc-950/90 backdrop-blur border border-pink-400/40 shadow-[0_0_24px_-8px_rgba(236,72,153,.6)] max-w-xs">
        <p className="text-xs font-black text-pink-300 uppercase tracking-widest">{sender_name || "Someone"} sent</p>
        <p className="text-sm text-white font-bold">{gift.name}</p>
        {message && <p className="text-[11px] text-zinc-300 mt-0.5 italic">&ldquo;{message}&rdquo;</p>}
      </div>
    </div>
  );
}

function HeroBurst({ burst, onDone }) {
  const { gift, sender_name, message } = burst;
  useEffect(() => {
    const t = setTimeout(onDone, 4200);
    return () => clearTimeout(t);
  }, [onDone]);

  const [particles] = useState(() =>
    Array.from({ length: 28 }, () => ({
      angle: Math.random() * Math.PI * 2,
      distance: 200 + Math.random() * 240,
    }))
  );
  return (
    <div className="pointer-events-none fixed inset-0 z-[90] grid place-items-center bg-black/45 backdrop-blur-sm">
      <div className="relative w-[280px] h-[280px] grid place-items-center">
        {particles.map((p, i) => (
          <Particle key={i} angle={p.angle} distance={p.distance} gift={gift} />
        ))}
        <div className={`rarity-${gift.rarity || "legendary"} rounded-full p-6 bg-zinc-950/80 backdrop-blur-md border border-yellow-400/40`}>
          <GiftIcon gift={gift} size={160} />
        </div>
      </div>
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 text-center">
        <p className="text-[10px] uppercase tracking-[0.3em] font-black text-yellow-300 mb-1">{sender_name || "Someone"} sent</p>
        <h2 className="text-4xl font-black tracking-tighter text-white">{gift.name}</h2>
        {message && <p className="text-sm text-zinc-300 mt-2 italic max-w-sm">&ldquo;{message}&rdquo;</p>}
      </div>
    </div>
  );
}

function FeastBurst({ burst, onDone }) {
  const { gift, sender_name, message } = burst;
  useEffect(() => {
    const t = setTimeout(onDone, 6000);
    return () => clearTimeout(t);
  }, [onDone]);

  // 60 emoji confetti drops — burger / pizza / cupcake / donut / gem etc.
  const [confetti] = useState(() => {
    const FOOD_BANK = ["🍔", "🍕", "🌮", "🧁", "🍩", "🍦", "💎", "✨", "🎉", "🍣"];
    return Array.from({ length: 60 }, () => ({
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 2.5 + Math.random() * 2,
      size: 22 + Math.random() * 36,
      emoji: FOOD_BANK[Math.floor(Math.random() * FOOD_BANK.length)],
    }));
  });

  return (
    <div className="pointer-events-none fixed inset-0 z-[95] overflow-hidden bg-gradient-to-b from-zinc-950/80 via-pink-950/40 to-cyan-950/60 backdrop-blur-md">
      {confetti.map((c, i) => (
        <span
          key={i}
          className="absolute top-0"
          style={{
            left: `${c.left}%`,
            fontSize: `${c.size}px`,
            animation: `feastDrop ${c.duration}s ${c.delay}s ease-in forwards`,
          }}
        >
          {c.emoji}
        </span>
      ))}
      <style>{`@keyframes feastDrop { 0%{transform:translateY(-10vh) rotate(0);opacity:0} 10%{opacity:1} 100%{transform:translateY(110vh) rotate(720deg);opacity:1} }`}</style>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="gift-hidden-feast inline-block text-[200px] mb-2">🎉</div>
          <p className="text-[10px] uppercase tracking-[0.3em] font-black text-pink-300">{sender_name || "Someone"} sent the legendary</p>
          <h1 className="bg-gradient-to-r from-pink-400 via-yellow-300 to-cyan-300 bg-clip-text text-transparent text-6xl font-black tracking-tighter mt-1">
            {gift.name}
          </h1>
          {message && <p className="text-base text-white/90 mt-3 italic max-w-md mx-auto">&ldquo;{message}&rdquo;</p>}
        </div>
      </div>
    </div>
  );
}

export default function GiftBurstProvider({ children }) {
  // queue burst events so concurrent gifts don't clobber each other
  const [queue, setQueue] = useState([]);
  const trigger = useCallback((event) => {
    if (!event?.gift) return;
    setQueue((q) => [...q, { ...event, id: Math.random().toString(36).slice(2) }]);
  }, []);
  const head = queue[0];
  const pop = useCallback(() => setQueue((q) => q.slice(1)), []);

  const value = useMemo(() => ({ trigger }), [trigger]);

  let burstUI = null;
  if (head) {
    const isFeast = head.gift.full_screen && (head.gift.rarity === "mythic" || head.gift.gift_id === "gift_hidden_feast");
    const isHero = head.gift.full_screen && !isFeast;
    burstUI = isFeast ? (
      <FeastBurst burst={head} onDone={pop} />
    ) : isHero ? (
      <HeroBurst burst={head} onDone={pop} />
    ) : (
      <InlineBurst burst={head} onDone={pop} />
    );
  }

  return (
    <GiftBurstCtx.Provider value={value}>
      {children}
      {burstUI}
    </GiftBurstCtx.Provider>
  );
}
